import { ReactElement } from 'react';

import {
	EvCaptionDefault,
	EvFlexColumn,
	EvTableColumn,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import { MFA_SESSION_SCAN_CREATED_AT_DAID } from 'data-automation-ids/MfaDashboardAutomationIds';
import { IMobileScansListRow } from 'src/apps/mobile-common/services/mobile-scans-list';
import { EvLinkLocal } from 'src/common/components/ev-link-local/EvLinkLocal';
import { stringCellFormatter } from 'src/common/components/ev-table/cell-formatters/string-cell-formatter/stringCellFormatter';
import { formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import { MFA_SESSION_SCANS_COLUMN_WIDTHS } from './MfaSessionScans.constants';
import { MFA_SESSION_SCANS_LABELS } from './MfaSessionScans.labels';
import { MfaSessionScanClickHandler } from './MfaSessionScans.types';

import './MfaSessionScans.scss';

const TOP_ALIGNED_CELL_STYLE = {
	alignItems: 'flex-start',
	verticalAlign: 'top'
};

const metricCellFormatter = (value: number): ReactElement => stringCellFormatter(String(value));

const screenshotCellFormatter = (
	screenshotUrl: string,
	scan: IMobileScansListRow
): ReactElement => {
	if (!screenshotUrl) {
		return (
			<span className="mfa-session-scans__screenshot-placeholder">
				{MFA_SESSION_SCANS_LABELS.NO_SCREENSHOT}
			</span>
		);
	}

	return (
		<img
			src={screenshotUrl}
			alt={MFA_SESSION_SCANS_LABELS.SCREENSHOT_ALT(scan.scanName)}
			className="mfa-session-scans__screenshot"
		/>
	);
};

const scanNameCellFormatter = (
	scanName: string,
	scan: IMobileScansListRow,
	onScanNameClick: MfaSessionScanClickHandler,
	currentPath: string
): ReactElement => {
	const scanCreatedAt = scan.scanCreatedAt ? formatShortMonthDatetime(scan.scanCreatedAt) : '';

	return (
		<EvFlexColumn>
			<EvLinkLocal
				className="mfa-session-scans__scan-name"
				linkText={scanName}
				url={RoutesHelper.getMfaSessionPath(scan.runId, SessionViews.ALL_ISSUES)}
				onClick={() => onScanNameClick(scan)}
				state={{ from: currentPath }}
			/>
			{scanCreatedAt && (
				<EvCaptionDefault
					data-automation-id={MFA_SESSION_SCAN_CREATED_AT_DAID}
					color={TEXT_COLOR.CONTENT_3}
				>
					{scanCreatedAt}
				</EvCaptionDefault>
			)}
		</EvFlexColumn>
	);
};

export const getMfaSessionScansColumns = (
	onScanNameClick: MfaSessionScanClickHandler,
	currentPath: string
): EvTableColumn[] => [
	{
		dataField: 'scanScreenshotUrl',
		text: MFA_SESSION_SCANS_LABELS.SCREENSHOT,
		style: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.SCREENSHOT, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.SCREENSHOT },
		formatter: screenshotCellFormatter
	},
	{
		dataField: 'scanName',
		text: MFA_SESSION_SCANS_LABELS.SCAN_NAME,
		style: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.SCAN_NAME, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.SCAN_NAME },
		sort: true,
		formatter: (scanName: string, scan: IMobileScansListRow) =>
			scanNameCellFormatter(scanName, scan, onScanNameClick, currentPath)
	},
	{
		dataField: 'totalIssues',
		text: MFA_SESSION_SCANS_LABELS.TOTAL_ISSUES,
		style: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'criticalIssues',
		text: MFA_SESSION_SCANS_LABELS.CRITICAL,
		style: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'seriousIssues',
		text: MFA_SESSION_SCANS_LABELS.SERIOUS,
		style: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'elementsWithIssues',
		text: MFA_SESSION_SCANS_LABELS.ELEMENTS_WITH_ISSUES,
		style: {
			width: MFA_SESSION_SCANS_COLUMN_WIDTHS.ELEMENTS_WITH_ISSUES,
			...TOP_ALIGNED_CELL_STYLE
		},
		headerStyle: { width: MFA_SESSION_SCANS_COLUMN_WIDTHS.ELEMENTS_WITH_ISSUES },
		sort: true,
		formatter: metricCellFormatter
	}
];
